// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-03-27 13:28:02 +0100 (Thu, 27 Mar 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: s.bock $
//  Revision  : $Rev: 151309 $
//  Purpose   : wiki components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Menu, Segment, List, Progress, Tab, Button} from 'semantic-ui-react'

// ---Framework imports
import log from 'uix/logging'
import {Sel, Actions} from 'uix/registries'
import {get, mapItems, items, dict, size, uniq, isFunction, isString, isNil} from 'ldash'
import {
  useSelector,
  useState,
  useEffect,
  useApi,
  useActions,
  usePartContext,
  useMemo,
  useRouter,
  useResettableState,
  useDebounce,
  usePersistentWithQueryOverride,
  useGraphics,
  usePhysicalNode,
} from 'uix/hooks'
import {hashString, formatDateTime, loadLazy} from 'uix/utils'
import {
  Toolbar,
  XMenu,
  TranslatedTable,
  ErrorBoundary,
  PortalTarget,
  AutoPortal,
  VIcon,
  XPopup,
  Link,
} from 'uix/components'
import {Editor} from 'uix/components/editor'
import {SingleFileUploader} from 'uix/upload/components'
import {TypeCaption} from 'uix/types/components'

// ---Local imports
import {WikiPane} from '../render'
import styles from '../wiki.scss'
import {MIME, MIMETYPES} from '../utils'
import {useCompletion, useWikiEditing, useWikiPreview} from '../hooks'
import classNames from 'classnames'

export {WikiModalTarget, WikiModalPortal} from './modal'

export const ChangeHistory = loadLazy(() => import(/* webpackChunkName: 'wiki' */ './history'))

//~ export {ChangeHistory} from './history'
// ---Globals

export const KeyMap = ({keymap}) => {
  let combined = dict([...items(keymap.pcDefault), ...items(keymap.sublime)])
  let divs = mapItems(
    combined,
    (shortcut, command) => (
      <List.Item key={shortcut}>
        <List.Content floated="right">
          <tt>{command}</tt>
        </List.Content>
        {shortcut}
      </List.Item>
    ),
    shortcut => shortcut !== 'fallthrough'
  )
  return (
    <Segment attached style={{height: '40vh', overflow: 'auto'}}>
      <List divided>{divs}</List>
    </Segment>
  )
}

export const BasicWikiEditor = ({
  otid: external_otid,
  showMenu = false,
  readOnly = false,
  autoComplete = false,
  lineWrapping = false,
  preview = false,
  previewStoreKey = null,
  prettyPrint = false,
  caption = false,
  debounceId = 0, // debounce external otids in ms
}) => {
  if (debounceId) {
    let [debounced_otid] = useDebounce(external_otid, debounceId, {leading: false})
    external_otid = debounced_otid
  }

  let {
    wikitext,
    setWikitext,
    setMimetype,
    saveWikiState,
    revertWikiState,
    saveEnabled,
    otid,
    datatype,
    mimetype,
    caption: nodeCaption,
  } = useWikiEditing({
    otid: external_otid,
  })

  showMenu = showMenu && saveWikiState !== null

  let callApi = useActions(Actions.callApi)

  let completion = useCompletion({mimetype})

  let codeChange = ({value}) => {
    setWikitext(value)
  }

  useWikiPreview({
    text: wikitext,
    otid,
    datatype,
    mimetype,
    active: !!preview && saveEnabled,
    storeKey: previewStoreKey,
  })

  let [editorCursor, setEditorCursor] = useState({line: 0, ch: 0})
  let cursorChange = ({editor, data}) => {
    setEditorCursor(data)
  }

  let opts = {readOnly, lineWrapping}

  let doPrettyPrint = () => {
    callApi(
      'prettyPrint',
      {text: wikitext, mimetype},
      ({prettyData}) => codeChange({value: prettyData}),
      {
        method: 'post',
      }
    )
  }

  let extraKeys = {}
  if (!readOnly) {
    extraKeys = {...extraKeys, 'Ctrl-S': saveWikiState}
  }
  if (autoComplete) {
    extraKeys = {...extraKeys, ...completion.extraKeys}
    opts = {...opts, hintOptions: completion.hintOptions}
  }

  let menu = null
  if (showMenu) {
    menu = (
      <Toolbar>
        {caption && <XMenu.Label content={nodeCaption || otid} />}
        <XMenu.BeginRight />
        <XMenu.Item
          icon="save"
          title="Save (Strg+S)"
          disabled={!saveEnabled}
          onClick={saveWikiState}
        />
        <XMenu.Item icon="undo" title="Undo" disabled={!saveEnabled} onClick={revertWikiState} />
        <WikiMimetype
          otid={otid}
          mimetype={mimetype}
          datatype={datatype}
          setMimetype={setMimetype}
        />
        {prettyPrint && (
          <XMenu.Item
            disabled={mimetype == MIME.MARKDOWN}
            onClick={doPrettyPrint}
            icon="prettyprint"
            title="Pretty Print"
          />
        )}
      </Toolbar>
    )
  }

  return (
    <>
      {menu}
      <Editor
        flex
        mimetype={mimetype}
        text={wikitext}
        opts={opts}
        extraKeys={extraKeys}
        onBeforeChange={codeChange}
        onCursor={cursorChange}
        cursor={editorCursor}
      />
    </>
  )
}

export const RevisionTable = ({
  accessor,
  revisions,
  onSelectionChange,
  indicator,
  canSelect,
  noDiffMode = false,
}) => {
  /* revisions should have datatype:
  [
    {date: string, username: string, someid: custom/accessor}
  ]
  */
  let {request} = useRouter()
  let get_id = rev => (accessor ? rev[accessor] : rev.id)
  let addDate = rev => ({...rev, date: new Date(rev.date), id: get_id(rev)})
  revisions = useMemo(() => revisions.map(addDate), [revisions])

  let [selectedRevs, setSelectedRevs] = usePersistentWithQueryOverride({
    name: 'revs',
    initialValue: '',
    replace: true,
  })
  let selected = useMemo(
    () => ({left: selectedRevs.split('-')[0], right: selectedRevs.split('-')[1]}),
    [selectedRevs]
  )
  let setSelected = s => {
    setSelectedRevs(`${s.left}-${s.right}`)
    if (onSelectionChange) {
      onSelectionChange(s)
    }
  }
  let is_selectable = canSelect || (r => true)

  // initialize selected revisions
  useEffect(() => {
    let revs_with_text = revisions.filter(r => is_selectable(r))
    if (size(revs_with_text) > 0) {
      let byId = dict(revs_with_text.map(r => [get_id(r), r]))
      if (byId[selected.left] && byId[selected.right]) {
        // persisted ids are in input revisions list, inform parent component via onSelectionChange
        setSelected({left: selected.left, right: selected.right})
      } else {
        let left = size(revs_with_text) > 1 ? get_id(revs_with_text[1]) : null
        // choose first ids
        setSelected({left, right: get_id(revs_with_text[0])})
      }
    }
  }, [get_id(revisions[0] || {})])

  let date = row => (
    <span>
      {indicator && indicator(row)}
      <span>{formatDateTime(row.date)}</span>
    </span>
  )

  let select = (row, side) => setSelected({...selected, [side]: row.id})

  let choose = row => {
    if (!is_selectable(row)) {
      return null
    }

    let leftCss = row.id == selected.left ? styles.histChoiceSelected : styles.histChoice
    let rightCss = row.id == selected.right ? styles.histChoiceSelected : styles.histChoice
    let isFirst = revisions[0].id == row.id
    let isLast = revisions.slice(-1)[0].id == row.id

    let leftDisabled = isFirst || selected.right == row.id
    let rightDisabled = isLast || selected.left == row.id

    leftCss = leftDisabled ? styles.histChoiceDisabled : leftCss
    rightCss = rightDisabled ? styles.histChoiceDisabled : rightCss

    return (
      <span title={row[accessor]}>
        <span className={leftCss} onClick={() => (!leftDisabled ? select(row, 'left') : null)}>
          &#x25ce;
        </span>
        <span className={rightCss} onClick={() => (!rightDisabled ? select(row, 'right') : null)}>
          &#x25ce;
        </span>
      </span>
    )
  }

  const getMimeType = row => {
    let rawMimeTypeString =
      row?.mimetype ?? revisions.filter(r => !isNil(r.text) && r.id < row.id)[0]?.mimetype
    if (rawMimeTypeString && rawMimeTypeString.search('hocr') !== -1) return 'hOCR'
    if (rawMimeTypeString && rawMimeTypeString.search('markdown') !== -1) return 'Markdown'
    return null
  }

  let i18n = useSelector(Sel.i18n)

  const columns = noDiffMode
    ? [
        {
          Header: i18n.get('sort_date'),
          id: 'date',
          accessor: row => formatDateTime(row.date),
        },
        {
          Header: 'User',
          accessor: 'username',
        },
        {
          Header: 'Format',
          id: 'format',
          accessor: row => getMimeType(row),
        },
      ]
    : [
        {
          Header: 'Date',
          id: 'date',
          accessor: row => date(row),
        },
        {
          Header: 'User',
          accessor: 'username',
        },
        {
          id: 'choose',
          maxWidth: 50,
          accessor: row => choose(row),
        },
      ]

  return (
    <TranslatedTable
      key={revisions.length}
      className={`-striped -highlight ${styles.historyTable}`}
      data={revisions}
      columns={columns}
      defaultPageSize={revisions.length}
      getTrProps={(state, row) => {
        if (!noDiffMode) return {}
        return {
          className: classNames(
            styles.selectableRow,
            row.original.id.toString() === selected.right ? styles.selected : null
          ),
          onClick: () => setSelected({...selected, right: row.original.id}),
        }
      }}
    />
  )
}

const ALLOWED_MIME = {
  1: [MIME.MARKDOWN, MIME.HTML],
  2: [MIME.MARKDOWN, MIME.HTML, MIME.XML],
  3: [MIME.MARKDOWN, MIME.HOCR],
  21: [MIME.MARKDOWN, MIME.HTML, MIME.XHTML],
}

export const WikiMimetype = ({otid, datatype, mimetype, setMimetype, icon}) => {
  let {reload} = usePartContext()
  let callApi = useActions('callApi')

  let onSetValue = mimetype => {
    if (setMimetype) {
      setMimetype(mimetype)
    } else {
      callApi(`/api/updateWikiMimetype`, {otid, mimetype}, reload)
    }
  }

  let options = mapItems(
    MIMETYPES,
    (mt, text) => ({value: mt, text}),
    mt => uniq([mimetype, ...(ALLOWED_MIME[datatype] ?? [])]).includes(mt)
  )
  return (
    <XMenu.Dropdown
      direction="left"
      icon={icon}
      title="Change Mimetype"
      onSetValue={onSetValue}
      value={mimetype}
      options={options}
    />
  )
}

export const ChildResourceUpload = ({
  otid,
  className,
  allowImages,
  allowAudioVideo,
  saveApi = 'wiki/insertChildResource', // or callable ({otid, uploadid}) =>
  deleteApi = 'wiki/deleteChildResource', // or callable ({otid}) =>
}) => {
  /* manage child resources for otid
   */
  let apiurl = useSelector(Sel.apiurl)
  let graphics = useGraphics(['types'])
  let callApi = useActions(Actions.callApi)

  let {data, reload} = useApi('wiki/getChildResources', {otid}, {condition: !!otid})

  let mimetypes = []
  if (allowImages) {
    mimetypes = [...mimetypes, ...['image/png', 'image/jpeg', 'image/webp']]
  }

  if (allowAudioVideo) {
    mimetypes = [...mimetypes, ...['video/mp4', 'audio/mpeg', 'image/webp']]
  }
  mimetypes = mimetypes.join(',')

  let [uploadResult, setUploadResult] = useResettableState(null, [otid])
  let [progress, setProgress] = useState(null)
  useEffect(() => {
    if (uploadResult?.state == 'upload done') {
      if (isFunction(saveApi)) {
        saveApi({otid, uploadid: uploadResult.uploadid, reloadComponent: reload})
      } else {
        callApi(saveApi, {parentid: otid, uploadid: uploadResult.uploadid}, reload)
      }
    }
  }, [uploadResult])
  let resources = data?.resources || []
  let webresources = resources.map(r => {
    let node = <TypeCaption {...r} caption={r.caption || r.fname} style={{paddingLeft: '5px'}} />
    // Popup needs another span to open?
    node = (
      <span style={{display: 'inline-flex', cursor: 'pointer'}} key={r.otid}>
        {node}
        <Button
          title="Delete"
          size="mini"
          secondary
          style={{color: 'var(--vl_body_color)'}}
          icon="trash"
          onClick={() => callApi(deleteApi, {resource_otid: r.otid}, reload)}
        />
      </span>
    )

    let popup = null
    let src = apiurl.href(`/download/webresource/${r.otid}`)

    if (r.subtype == 2) {
      popup = <img style={{width: '10vw'}} src={src} />
    } else if (r.mimetype == 'video/mp4') {
      popup = (
        <video controls width="300">
          <source src={src} type="video/mp4" />
        </video>
      )
    } else if (r.mimetype == 'audio/mpeg') {
      popup = (
        <audio controls>
          <source src={src} type="audio/mpeg" />
        </audio>
      )
    }
    return popup ? (
      <XPopup key={r.otid} trigger={node} hoverable>
        {popup}
      </XPopup>
    ) : (
      node
    )
  })

  if (!graphics.types) return null

  return (
    <div className={className}>
      {webresources}{' '}
      <SingleFileUploader
        onUploadDone={setUploadResult}
        onUploadProgress={setProgress}
        as="span"
        mimetypes={mimetypes}
      >
        <span>
          <VIcon name="ws_add-file" />
          Upload
        </span>
      </SingleFileUploader>
      {progress?.active && <Progress percent={progress.percentage} attached="bottom" size="tiny" />}
    </div>
  )
}

export const WikiAlternatives = ({}) => {
  let {datatype, ...r} = usePhysicalNode()
  let {request, go} = useRouter()
  let dcfg = useSelector(Sel.dconfig)
  if (datatype == 10) {
    let activeIndex = null
    let panes = dcfg.domain.wikipages.map((ident, i) => {
      if (request.id == ident) {
        activeIndex = i
      }
      return {
        menuItem: (
          <Menu.Item key={ident} as="span">
            <Link id={ident}>{ident}</Link>
          </Menu.Item>
        ),
        wiki_ident: ident,
      }
    })

    return (
      <Tab
        menu={{attached: 'bottom', style: {width: '99.99%', borderRight: '0px'}}}
        panes={panes}
        activeIndex={activeIndex}
      />
    )
  }

  return null
}

export const DebugButton = loadLazy(() => import(/* webpackChunkName: 'wiki' */ './debugdlg'))
export const WikiPreview = loadLazy(() => import(/* webpackChunkName: 'wiki' */ './preview'))
